import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    ClipboardCheck, FileDown, FileText, FileSpreadsheet, Presentation,
    ShieldCheck, Sparkles, ArrowRight,
} from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const FORMATS = [
    { key: "pdf",  label: "PDF",         icon: FileDown },
    { key: "docx", label: "Word",        icon: FileText },
    { key: "pptx", label: "PowerPoint",  icon: Presentation },
    { key: "xlsx", label: "Excel",       icon: FileSpreadsheet },
];

export default function BoardBrief() {
    const [brief, setBrief] = useState(null);
    const [busy, setBusy] = useState(false);
    const [downloading, setDownloading] = useState(null);

    useEffect(() => { generate(); /* eslint-disable-next-line */ }, []);

    const generate = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/edi/board-brief", { period: "Feb 2026" });
            setBrief(data);
        } catch {
            toast.error("Board Brief generation failed");
        } finally {
            setBusy(false);
        }
    };

    const download = async (fmt) => {
        if (!brief) return;
        setDownloading(fmt);
        try {
            const token = localStorage.getItem("sgsb.token");
            const resp = await fetch(`${API_BASE}/reports/export`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    title: `SGS Bateman Executive Board Brief · ${brief.period}`,
                    content: brief.markdown,
                    fmt,
                    confidentiality: "Board",
                }),
            });
            if (!resp.ok) throw new Error(String(resp.status));
            const cd = resp.headers.get("Content-Disposition") || "";
            const match = cd.match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : `SGSBateman_Board_Brief.${fmt}`;
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 3000);
            toast.success(`Board Brief · ${fmt.toUpperCase()} downloaded`);
        } catch {
            toast.error(`Board Brief · ${fmt.toUpperCase()} export failed`);
        } finally {
            setDownloading(null);
        }
    };

    return (
        <div className="space-y-8" data-testid={EDI.boardBriefRoot}>
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        Board Brief™
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-2xl font-body text-sm">
                        A premium, one-click executive board brief — 15 executive sections composed
                        automatically from live portfolio, engineering and knowledge intelligence.
                        Board-ready cover, confidentiality footer, and four executive formats.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)] text-xs font-body">
                    <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">One click · four formats</span>
                </div>
            </div>

            <div className="glass rounded-xl p-6 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 90% 10%, rgba(212,175,55,0.24), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(59,130,246,0.16), transparent 55%)",
                    }}
                />
                <div className="relative flex flex-wrap items-center gap-3">
                    <button
                        data-testid={EDI.boardBriefGenerate}
                        onClick={generate}
                        disabled={busy}
                        className="inline-flex items-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-5 py-2.5 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 transition-colors duration-200"
                    >
                        <ClipboardCheck className="h-4 w-4" strokeWidth={2} />
                        {busy ? "Composing Board Brief…" : "Recompose Board Brief"}
                    </button>
                    {FORMATS.map(({ key, label, icon: Icon }) => (
                        <button
                            key={key}
                            data-testid={EDI.boardBriefExport(key)}
                            onClick={() => download(key)}
                            disabled={!brief || !!downloading}
                            className="inline-flex items-center gap-2 rounded-md border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)] px-4 py-2 font-display font-bold text-xs hover:bg-[color:var(--sgsb-gold)]/10 disabled:opacity-40 transition-colors duration-200"
                        >
                            <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                            {downloading === key ? "Preparing…" : label}
                        </button>
                    ))}
                </div>
            </div>

            {brief && (
                <div className="glass rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        <h3 className="font-display font-black text-white tracking-tight">
                            Board Brief · {brief.period}
                        </h3>
                        <div className="flex-1" />
                        <span className="kicker text-[10px] text-slate-500">
                            {brief.sections.length} executive sections
                        </span>
                    </div>
                    <div className="grid grid-cols-1 xl:grid-cols-2">
                        <div className="p-6 border-r border-slate-800/60">
                            <div className="kicker text-slate-500 text-[10px] mb-3">Executive sections</div>
                            <ol className="space-y-1.5 text-sm text-slate-200 font-body">
                                {brief.sections.map((s, i) => (
                                    <li key={s} className="flex items-center gap-2">
                                        <ArrowRight className="h-3 w-3 text-[var(--sgsb-gold)]" strokeWidth={2} />
                                        <span className="text-slate-500 text-xs">{String(i + 1).padStart(2, "0")}</span>
                                        <span>{s}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                        <div className="p-6 max-h-[520px] overflow-auto">
                            <div className="kicker text-slate-500 text-[10px] mb-3">Preview</div>
                            <pre className="whitespace-pre-wrap text-[13px] text-slate-300 font-body leading-relaxed">{brief.markdown}</pre>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
